<template>
  <div class="panel place-detail-sensor">
    <div class="panel-content">
      <header class="title-bar title-bar-between">
        <h1 class="title" v-t="'appPanelPlaceDetailSensor.sensors'" />
        <div class="title-bar-action">
          <span class="label" v-t="'appPanelPlaceDetailSensor.display'" />
          <sensor-group-and-sensor-selector
            id-prefix="place"
            v-bind:place-id="placeId"
            class="selector"
            v-on:selected="$_onSelected"
          />
        </div>
      </header>
      <div class="content-wrapper">
        <section class="latest-values-container">
          <sensor-latest-values
            v-bind:sensor-group-ids="sensorGroupIds"
            v-bind:sensor-ids="sensorIds"
            v-bind:place-id="placeId"
            v-bind:current-time="currentTime"
          />
        </section>
        <div class="separator" />
        <section class="chart-container">
          <sensor-chart
            v-bind:sensor-ids="sensorIds"
            v-bind:place-id="placeId"
            v-bind:current-time="currentTime"
          />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import SensorGroupAndSensorSelector from '@/components/sensor/AppSensorGroupAndSensorSelector.vue'
import SensorLatestValues from '@/components/sensor/AppSensorLatestValues.vue'
import SensorChart from '@/components/sensor/AppSensorChart.vue'

export default {
  name: 'AppPanelPlaceDetailSensor',
  components: {
    'sensor-group-and-sensor-selector': SensorGroupAndSensorSelector,
    'sensor-latest-values': SensorLatestValues,
    'sensor-chart': SensorChart,
  },
  props: {
    /**
     * プレイスデータ
     */
    place: {
      type: Object,
      default: null,
    },
    /**
     * インターバル間隔(ms)
     */
    interval: {
      type: Number,
      default: 30000,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // 現在時刻(moment)
      currentTime: moment(),
      // インターバルID
      intervalId: null,
      // センサーグループ選択値
      sensorGroupIds: null,
      // センサーID選択値
      sensorIds: null,
    }
  },
  computed: {
    /**
     * プレイスID
     * @type {Number}
     */
    placeId: function () {
      return (this.place && this.place.id) || null
    },
  },
  watch: {
    interval: {
      immediate: true,
      handler: function () {
        this.$_startCurrentTimeAutoUpdate()
      },
    },
  },
  created: function () {
    this.$_updateSensors()
    this.$_updateSensorItems()
    this.$_updateSensorGroups()
  },
  beforeDestroy: function () {
    this.$_stopCurrentTimeAutoUpdate()
  },
  methods: {
    /**
     * センサーデータ更新
     */
    $_updateSensors: async function () {
      try {
        await this.$store.dispatch('sensor/updateSensorsWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch sensors. use fetched before.')
        })
      }
    },

    /**
     * センサー測定項目データ更新
     */
    $_updateSensorItems: async function () {
      try {
        await this.$store.dispatch('sensorItem/updateSensorItemsWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch sensor items. use fetched before.')
        })
      }
    },

    /**
     * センサーグループデータ更新
     */
    $_updateSensorGroups: async function () {
      try {
        await this.$store.dispatch('sensorGroup/updateSensorGroupsWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch sensor groups. use fetched before.')
        })
      }
    },

    /**
     * 現在時刻の自動更新を開始する
     */
    $_startCurrentTimeAutoUpdate: function () {
      this.intervalId && this.$_stopCurrentTimeAutoUpdate()
      this.intervalId = setInterval(() => {
        this.$log.debug('[DEBUG] currentTime for sensor update')
        this.currentTime = moment()
      }, this.interval)
      this.$log.info('[started] currentTime update for sensor')
    },

    /**
     * 現在時刻の自動更新を終了する
     */
    $_stopCurrentTimeAutoUpdate: function () {
      this.intervalId && clearInterval(this.intervalId)
      this.intervalId = null
      this.$log.info('[stopped] currentTime update for sensor')
    },

    /**
     * センサーグループ,センサー選択時処理
     * @param {Object} value 選択値
     */
    $_onSelected: function (value) {
      if (value) {
        const { sensorId, sensorGroupId } = value
        this.sensorIds = sensorId
        this.sensorGroupIds = sensorGroupId
      } else {
        this.sensorIds = null
        this.sensorGroupIds = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../assets/scss/parts/title-bar';

$-title-bar-height: 3rem;
$-border: 1px solid #cccccc;

.panel {
  width: 100%;
  height: 100%;
  color: $text-color;
  background-color: $dialog-background-color;
}

.place-detail-sensor {
  padding: 22px;
}

.panel-content {
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  border: $-border;
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr);
  grid-auto-rows: 1fr;
}

.title-bar {
  height: $-title-bar-height;
  border-bottom: $-border;
  padding: 0 20px;

  .title {
    font-size: $font-size-middle;
    letter-spacing: 0;
  }

  .title-bar-action {
    width: calc(50% + 2rem);
  }

  .label {
    width: 2rem;
    font-size: $font-size-small;
    color: $text-color;
    font-weight: $font-weight-bold;
    margin-right: 8px;
    line-height: 1.5;
    white-space: nowrap;
  }

  .selector {
    width: 100%;
    flex-grow: 1;
  }
}

.content-wrapper {
  width: 100%;
  height: 100%;

  // HACK:スクロールバーの有無によってレイアウトシフトを起こさない
  // 本来は `scrollbar-gutter: stable both-edges` を使いたいが、
  // Chromeで使用するとスクロールバー出現側のみ子エレメントの描画が gutter 幅分だけ切れてしまうので
  // スクロールバーを完全透明で常に出しておいて、padding をスクロールバー分狭く設定する
  overflow-x: hidden;
  overflow-y: scroll;
  @include mixin-scrollbar(transparent);
  & {
    padding: 12px 10px 12px 20px;
  }
}

.latest-values-container {
  width: 100%;
  height: auto;
  padding: 0;
}

.separator {
  width: 100%;
  border-top: 1px dashed #cccccc;
  margin: 12px 0;
}

.chart-container {
  width: 100%;
  height: auto;
}
</style>
